"use client";

// Unique mini product-UI mockups per use case, composed from HeroUI primitives.
// Keyed by use-case name; cards fall back to the PNG image or UseCaseArt when
// a mockup isn't registered yet.

import type { ReactNode } from "react";
import { Chip } from "@heroui/react";
import {
  Check,
  X,
  Mail,
  Shield,
  AlertTriangle,
  RefreshCw,
  ArrowRight,
  Bell,
  Lock,
  Key,
  User,
  Users,
  Eye,
  Radar,
  Clock,
  FileText,
  Code,
  Building2,
  SparkleFilled,
  Link,
  QrCode,
  ImageIcon,
  ExternalLink,
  Fingerprint,
  Globe2,
  Search,
  Server,
  BranchFilled,
} from "@/components/icons";

/* ── Shared primitives ─────────────────────────────── */

function Bar({ w = "100%", className = "" }: { w?: string; className?: string }) {
  return (
    <div
      className={`h-1.5 rounded-full bg-neutral-200 dark:bg-neutral-700 ${className}`}
      style={{ width: w }}
    />
  );
}

function Ok() {
  return (
    <span className="flex size-4 items-center justify-center rounded-full bg-success/15">
      <Check className="size-2.5 text-success" />
    </span>
  );
}

function Bad() {
  return (
    <span className="flex size-4 items-center justify-center rounded-full bg-danger/15">
      <X className="size-2.5 text-danger" />
    </span>
  );
}

/** A row that looks like a list/table entry. */
function MockRow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-lg border border-neutral-200/70 bg-white px-2.5 py-1.5 dark:border-neutral-700/60 dark:bg-surface-raised ${className}`}
    >
      {children}
    </div>
  );
}

const MONO = "font-mono text-3xs text-text-secondary";

/* ── Email validity ────────────────────────────────── */

function SignupValidation() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Create account</p>
      <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-white px-2.5 py-2 dark:bg-surface-raised">
        <Mail className="size-3 text-text-quaternary" />
        <span className={MONO}>sara@acme.com</span>
        <span className="ml-auto"><Ok /></span>
      </div>
      <Chip size="sm" variant="soft" color="success" className="text-4xs">Deliverable, safe to accept</Chip>
      <div className="rounded-lg bg-brand py-1.5 text-center text-3xs font-semibold text-brand-foreground">Sign up</div>
    </div>
  );
}

function ListCleaning() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow><span className={MONO}>jules@stripe.com</span><span className="ml-auto"><Ok /></span></MockRow>
      <MockRow className="opacity-60"><span className={`${MONO} line-through`}>old@defunct.io</span><span className="ml-auto"><Bad /></span></MockRow>
      <MockRow><span className={MONO}>ana@vercel.com</span><span className="ml-auto"><Ok /></span></MockRow>
      <div className="flex justify-end pt-0.5">
        <Chip size="sm" variant="soft" color="accent" className="text-4xs">1 removed · 2 kept</Chip>
      </div>
    </div>
  );
}

function BounceReduction() {
  return (
    <div className="w-full max-w-56">
      <div className="flex items-end justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Bounce rate</p>
        <Chip size="sm" variant="soft" color="success" className="text-4xs">↓ 0.4%</Chip>
      </div>
      {/* descending bars */}
      <div className="mt-2 flex h-16 items-end gap-1.5">
        {[100, 82, 65, 48, 34, 22, 12].map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-sm ${i < 5 ? "bg-neutral-200 dark:bg-neutral-700" : "bg-success/70"}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-4xs text-text-quaternary"><span>Jan</span><span>Jul</span></div>
    </div>
  );
}

function CrmHygiene() {
  // Fixed-width status column so every bar ends, and every chip right-aligns,
  // on the same x.
  const rows = [
    { label: "Verified today", color: "success" as const },
    { label: "Verified 2d ago", color: "success" as const },
    { label: "Re-checking…", color: "warning" as const },
  ];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Contacts</p>
        <RefreshCw className="size-3 text-text-quaternary" />
      </div>
      {rows.map(({ label, color }) => (
        <MockRow key={label}>
          <div className="size-4 shrink-0 rounded-full bg-neutral-200 dark:bg-neutral-700" />
          <Bar className="flex-1" />
          <span className="flex w-22 shrink-0 justify-end">
            <Chip size="sm" variant="soft" color={color} className="text-4xs">{label}</Chip>
          </span>
        </MockRow>
      ))}
    </div>
  );
}

function FraudScreening() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-danger/40">
        <span className={MONO}>x9f2k@tempmail.lol</span>
        <span className="ml-auto"><Bad /></span>
      </MockRow>
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="size-3 text-danger" />
        <span className="text-3xs text-danger">Disposable domain detected</span>
      </div>
      <div className="flex gap-1.5">
        <div className="flex-1 rounded-lg bg-danger py-1.5 text-center text-3xs font-semibold text-white">Block</div>
        <div className="flex-1 rounded-lg border border-neutral-200 py-1.5 text-center text-3xs font-medium text-text-tertiary dark:border-neutral-700">Review</div>
      </div>
    </div>
  );
}

function RecoveryChecks() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2">
        <span className="flex size-6 items-center justify-center rounded-lg bg-brand/10">
          <Shield className="size-3.5 text-brand" />
        </span>
        <p className="text-3xs font-semibold text-text-secondary">Account recovery</p>
      </div>
      <MockRow>
        <span className={MONO}>backup@sara.me</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
      <Chip size="sm" variant="soft" color="success" className="text-4xs">Still reachable, checked weekly</Chip>
    </div>
  );
}

function SellerVerification() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="size-8 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div className="flex-1 space-y-1.5">
          <Bar w="60%" />
          <Bar w="40%" className="opacity-60" />
        </div>
        <Chip size="sm" variant="soft" color="success" className="gap-1 text-4xs">
          <Check className="size-2.5" /> Verified seller
        </Chip>
      </div>
      <MockRow>
        <Mail className="size-3 text-text-quaternary" />
        <span className={MONO}>shop@maker.store</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

function TypoCorrection() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-warning/40">
        <span className={MONO}>sara@gmial.com</span>
        <AlertTriangle className="ml-auto size-3 text-warning" />
      </MockRow>
      <div className="flex items-center gap-1.5 pl-1">
        <ArrowRight className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">Did you mean</span>
        <Chip size="sm" variant="soft" color="accent" className="font-mono text-4xs">sara@gmail.com</Chip>
        <span className="text-3xs text-text-secondary">?</span>
      </div>
    </div>
  );
}

function TrialAbuse() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {["trial1@burner.cc", "trial2@burner.cc", "trial3@burner.cc"].map((e, i) => (
        <MockRow key={e} className={i === 2 ? "border-danger/40" : ""}>
          <span className={MONO}>{e}</span>
          <span className="ml-auto text-4xs text-text-quaternary">+{i + 1}hr</span>
        </MockRow>
      ))}
      <div className="flex justify-end pt-0.5">
        <Chip size="sm" variant="soft" color="danger" className="gap-1 text-4xs">
          <AlertTriangle className="size-2.5" /> 3 trials, same device
        </Chip>
      </div>
    </div>
  );
}

function AddressUpdates() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Update email</p>
      <MockRow className="opacity-60">
        <span className={`${MONO} line-through`}>s.lane@oldjob.com</span>
      </MockRow>
      <div className="flex justify-center">
        <ArrowRight className="size-3 rotate-90 text-text-quaternary" />
      </div>
      <MockRow className="border-success/40">
        <span className={MONO}>sara@newmail.com</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

/* ── Email-to-person ─────────────────────────────────── */

function ProfileCard({ chip, sub }: { chip: ReactNode; sub?: ReactNode }) {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="size-8 shrink-0 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div className="flex-1 space-y-1.5">
          <Bar w="62%" />
          <Bar w="40%" className="opacity-60" />
        </div>
        {chip}
      </div>
      {sub}
    </div>
  );
}

function LeadEnrichment() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <Mail className="size-3 text-text-quaternary" />
        <span className={MONO}>j.chen@nova.ai</span>
      </MockRow>
      <div className="flex justify-center">
        <ArrowRight className="size-3 rotate-90 text-text-quaternary" />
      </div>
      <MockRow>
        <div className="size-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div className="space-y-1">
          <p className="text-3xs font-semibold text-text-primary">Jamie Chen</p>
          <p className="text-4xs text-text-tertiary">VP Sales · Nova AI</p>
        </div>
        <SparkleFilled className="ml-auto size-3 text-brand" />
      </MockRow>
    </div>
  );
}

function FormIdentification() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Demo request</p>
      <MockRow>
        <span className={MONO}>m.okafor@finch.co</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Identified</Chip>
      </MockRow>
      <div className="flex items-center gap-1.5 pl-1">
        <User className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">Head of Data · Finch · 200–500</span>
      </div>
      <Chip size="sm" variant="soft" color="success" className="text-4xs">Route to sales → priority</Chip>
    </div>
  );
}

function AccountMatching() {
  const rows = [
    { email: "ana@vercel.com", company: "Vercel" },
    { email: "raj@stripe.com", company: "Stripe" },
  ];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {rows.map(({ email, company }) => (
        <MockRow key={email}>
          <span className={`${MONO} flex-1 truncate`}>{email}</span>
          <ArrowRight className="size-3 shrink-0 text-text-quaternary" />
          <span className="flex w-16 shrink-0 justify-end">
            <Chip size="sm" variant="soft" color="accent" className="gap-1 text-4xs">
              <Building2 className="size-2.5" /> {company}
            </Chip>
          </span>
        </MockRow>
      ))}
      <div className="flex justify-end pt-0.5">
        <Chip size="sm" variant="soft" color="success" className="text-4xs">2 target accounts matched</Chip>
      </div>
    </div>
  );
}

function SupportContext() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Ticket #4821</p>
        <Clock className="size-3 text-text-quaternary" />
      </div>
      <MockRow>
        <div className="size-5 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div>
          <p className="text-3xs font-semibold text-text-primary">Priya N.</p>
          <p className="text-4xs text-text-tertiary">CTO · Relay · Enterprise plan</p>
        </div>
      </MockRow>
      <Bar w="100%" />
      <Bar w="70%" className="opacity-60" />
    </div>
  );
}

function CrmEnrichment() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {["+ Title", "+ Company", "+ Industry"].map((label) => (
        <MockRow key={label}>
          <div className="size-4 shrink-0 rounded-full bg-neutral-200 dark:bg-neutral-700" />
          <Bar className="flex-1" />
          <span className="flex w-16 shrink-0 justify-end">
            <Chip size="sm" variant="soft" color="success" className="text-4xs">{label}</Chip>
          </span>
        </MockRow>
      ))}
    </div>
  );
}

function InvestorResearch() {
  return (
    <ProfileCard
      chip={<Chip size="sm" variant="soft" color="accent" className="text-4xs">Founder</Chip>}
      sub={
        <MockRow>
          <span className={MONO}>hello@stealth.xyz</span>
          <span className="ml-auto text-4xs text-text-tertiary">ex-Google · 2nd time</span>
        </MockRow>
      }
    />
  );
}

function CandidateResearch() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <ProfileCard chip={<Chip size="sm" variant="soft" color="accent" className="text-4xs">Senior SWE</Chip>} />
      <div className="space-y-1.5 pl-1">
        {["Nova AI · 2y", "Stripe · 3y"].map((role) => (
          <div key={role} className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
            <span className="text-4xs text-text-tertiary">{role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AbuseInvestigation() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-danger/40">
        <span className={MONO}>@shadow_user_99</span>
        <AlertTriangle className="ml-auto size-3 text-danger" />
      </MockRow>
      <div className="flex justify-center">
        <ArrowRight className="size-3 rotate-90 text-text-quaternary" />
      </div>
      <MockRow>
        <User className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">Real identity resolved</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

function EmployerVerification() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-3xs text-text-secondary">Claims:</span>
        <Chip size="sm" variant="soft" className="text-4xs">Works at Acme Corp</Chip>
      </div>
      <MockRow>
        <Mail className="size-3 text-text-quaternary" />
        <span className={MONO}>d.kim@acmecorp.com</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
      <Chip size="sm" variant="soft" color="success" className="gap-1 text-4xs">
        <Check className="size-2.5" /> Employment confirmed
      </Chip>
    </div>
  );
}

function PersonalizedOnboarding() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-sm font-semibold text-text-primary">Welcome, Sara 👋</p>
      <p className="text-4xs text-text-tertiary">Set up for marketing leads · 51–200 team</p>
      <div className="space-y-1.5">
        {["Connect your CRM", "Invite your team"].map((step, i) => (
          <MockRow key={step}>
            <span className="flex size-4 items-center justify-center rounded-full bg-brand/10 text-4xs font-semibold text-brand">{i + 1}</span>
            <span className="text-3xs text-text-secondary">{step}</span>
          </MockRow>
        ))}
      </div>
    </div>
  );
}

function DuplicateDetection() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow><span className={MONO}>sara@work.com</span></MockRow>
      <MockRow><span className={MONO}>sara.l@gmail.com</span></MockRow>
      <div className="flex items-center justify-center gap-1.5">
        <Users className="size-3 text-brand" />
        <span className="text-3xs font-medium text-brand">Same person, merge records</span>
      </div>
    </div>
  );
}

function LeadRouting() {
  // Email flexes so the arrows lock to one x; fixed chip column right-aligns them.
  const rows = [
    { email: "lead@fintech.io", label: "Fintech team", color: "accent" as const },
    { email: "ceo@bigco.com", label: "Enterprise", color: "warning" as const },
  ];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {rows.map(({ email, label, color }) => (
        <MockRow key={email}>
          <span className={`${MONO} flex-1 truncate`}>{email}</span>
          <ArrowRight className="size-3 shrink-0 text-text-quaternary" />
          <span className="flex w-20 shrink-0 justify-end">
            <Chip size="sm" variant="soft" color={color} className="text-4xs">{label}</Chip>
          </span>
        </MockRow>
      ))}
      <div className="flex justify-end pt-0.5">
        <Chip size="sm" variant="soft" color="success" className="gap-1 text-4xs"><Check className="size-2.5" /> Auto-routed</Chip>
      </div>
    </div>
  );
}

/* ── Email breach ────────────────────────────────────── */

function BreachAlerts() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-danger/40">
        <span className="flex size-6 items-center justify-center rounded-lg bg-danger/10">
          <Bell className="size-3.5 text-danger" />
        </span>
        <div>
          <p className="text-3xs font-semibold text-text-primary">New breach: DataCo</p>
          <p className="text-4xs text-text-tertiary">Your email was exposed · just now</p>
        </div>
      </MockRow>
      <div className="rounded-lg bg-brand py-1.5 text-center text-3xs font-semibold text-brand-foreground">Secure account</div>
    </div>
  );
}

function RiskScoring() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-end justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Identity risk</p>
        <span className="text-lg font-bold text-danger">78<span className="text-3xs font-medium text-text-quaternary">/100</span></span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
        <div className="h-full w-[78%] rounded-full bg-linear-to-r from-warning to-danger" />
      </div>
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="size-3 text-danger" />
        <span className="text-4xs text-text-tertiary">4 prior breaches · manual review</span>
      </div>
    </div>
  );
}

function PasswordReset() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-danger/40">
        <AlertTriangle className="size-3 text-danger" />
        <span className="text-3xs text-text-secondary">Breach affects 3 users</span>
      </MockRow>
      <div className="flex justify-center">
        <ArrowRight className="size-3 rotate-90 text-text-quaternary" />
      </div>
      <MockRow className="border-success/40">
        <RefreshCw className="size-3 text-success" />
        <span className="text-3xs text-text-secondary">Password resets sent</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

function MfaEnforcement() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <span className={MONO}>sam@corp.com</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Breached</Chip>
      </MockRow>
      <MockRow className="border-brand/40">
        <span className="flex size-6 items-center justify-center rounded-lg bg-brand/10">
          <Lock className="size-3.5 text-brand" />
        </span>
        <span className="text-3xs font-medium text-text-primary">MFA now required</span>
      </MockRow>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <span key={i} className="flex h-6 flex-1 items-center justify-center rounded-md border border-neutral-200 text-3xs font-semibold text-text-secondary dark:border-neutral-700">{i < 4 ? "•" : ""}</span>
        ))}
      </div>
    </div>
  );
}

function EmployeeMonitoring() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow><span className={MONO}>li@corp.com</span><span className="ml-auto"><Ok /></span></MockRow>
      <MockRow className="border-danger/40">
        <span className={MONO}>ben@corp.com</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto gap-1 text-4xs"><Bell className="size-2.5" /> Alerted</Chip>
      </MockRow>
      <MockRow><span className={MONO}>ivy@corp.com</span><span className="ml-auto"><Ok /></span></MockRow>
    </div>
  );
}

function VendorAssessment() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {([
        ["Billing SaaS", "0 breaches", true],
        ["Analytics Inc", "2 breaches", false],
      ] as const).map(([name, label, ok]) => (
        <MockRow key={name}>
          <Building2 className="size-3 text-text-quaternary" />
          <span className="text-3xs text-text-secondary">{name}</span>
          <Chip size="sm" variant="soft" color={ok ? "success" : "warning"} className="ml-auto text-4xs">{label}</Chip>
        </MockRow>
      ))}
      <div className="flex justify-end pt-0.5">
        <span className="text-4xs text-text-quaternary">Reviewed before renewal</span>
      </div>
    </div>
  );
}

function IdentityProtection() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2">
        <span className="relative flex size-7 items-center justify-center rounded-full bg-success/10">
          <Radar className="size-4 text-success" />
        </span>
        <div>
          <p className="text-3xs font-semibold text-text-primary">Monitoring active</p>
          <p className="text-4xs text-text-tertiary">2 addresses watched</p>
        </div>
      </div>
      <MockRow className="border-success/40">
        <span className="text-3xs text-text-secondary">No new exposures this month</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

function BreachReporting() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {[
        ["DataCo", "2024", "emails · passwords"],
        ["ShopLeak", "2022", "emails · phones"],
      ].map(([name, year, data]) => (
        <MockRow key={name}>
          <FileText className="size-3 text-text-quaternary" />
          <div>
            <p className="text-3xs font-semibold text-text-primary">{name} · {year}</p>
            <p className="text-4xs text-text-tertiary">Exposed: {data}</p>
          </div>
        </MockRow>
      ))}
    </div>
  );
}

function IncidentInvestigation() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      {([
        ["Suspicious login", "02:14", false],
        ["Email in breach dump", "02:15", true],
        ["Session revoked", "02:16", false],
      ] as const).map(([label, t, hot]) => (
        <div key={label} className="flex items-center gap-2">
          <span className={`size-1.5 rounded-full ${hot ? "bg-danger" : "bg-neutral-300 dark:bg-neutral-600"}`} />
          <span className="text-3xs text-text-secondary">{label}</span>
          <span className="ml-auto font-mono text-4xs text-text-quaternary">{t}</span>
        </div>
      ))}
      <div className="pt-1">
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Breach data linked to incident</Chip>
      </div>
    </div>
  );
}

function CyberUnderwriting() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Acme Corp</p>
        <Chip size="sm" variant="soft" color="warning" className="text-4xs">Moderate risk</Chip>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div className="rounded-lg border border-neutral-200/70 bg-white p-2 dark:border-neutral-700/60 dark:bg-surface-raised">
          <p className="text-sm font-bold text-text-primary">7</p>
          <p className="text-4xs text-text-tertiary">Breaches, 5y</p>
        </div>
        <div className="rounded-lg border border-neutral-200/70 bg-white p-2 dark:border-neutral-700/60 dark:bg-surface-raised">
          <p className="text-sm font-bold text-text-primary">+18%</p>
          <p className="text-4xs text-text-tertiary">Premium adj.</p>
        </div>
      </div>
    </div>
  );
}

function DomainMonitoring() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <span className={MONO}>@acme.com</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Scanning…</Chip>
      </MockRow>
      <div className="flex items-center justify-between rounded-lg bg-danger/5 px-2.5 py-2">
        <span className="text-3xs text-text-secondary">Breached addresses found</span>
        <span className="text-sm font-bold text-danger">14</span>
      </div>
      <Bar w="100%" className="opacity-60" />
    </div>
  );
}

function ExecutiveProtection() {
  return (
    <ProfileCard
      chip={
        <Chip size="sm" variant="soft" color="accent" className="gap-1 text-4xs">
          <Shield className="size-2.5" /> Watchlist
        </Chip>
      }
      sub={
        <MockRow className="border-success/40">
          <Eye className="size-3 text-success" />
          <span className="text-3xs text-text-secondary">CEO · monitored 24/7</span>
          <span className="ml-auto"><Ok /></span>
        </MockRow>
      }
    />
  );
}

/* ── Password breach ─────────────────────────────────── */

function PasswordField({ state, note }: { state: "ok" | "bad"; note: string }) {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Choose password</p>
      <div className={`flex items-center gap-2 rounded-lg border bg-white px-2.5 py-2 dark:bg-surface-raised ${state === "ok" ? "border-success/40" : "border-danger/40"}`}>
        <Lock className="size-3 text-text-quaternary" />
        <span className={MONO}>••••••••••</span>
        <span className="ml-auto">{state === "ok" ? <Ok /> : <Bad />}</span>
      </div>
      <Chip size="sm" variant="soft" color={state === "ok" ? "success" : "danger"} className="text-4xs">{note}</Chip>
    </div>
  );
}

function PasswordScreening() {
  return <PasswordField state="bad" note="Found in 3 breaches, choose another" />;
}

function ResetScreening() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Reset password</p>
      <MockRow className="border-danger/40 opacity-60">
        <Lock className="size-3 text-text-quaternary" />
        {/* Bullet glyphs sit higher than the font's strike position, so an
            overlaid rule centres on the dots where `line-through` would not. */}
        <span className={`${MONO} relative`}>
          ••••••••
          <span aria-hidden className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-current" />
        </span>
        <span className="ml-auto"><Bad /></span>
      </MockRow>
      <MockRow className="border-success/40">
        <Key className="size-3 text-text-quaternary" />
        <span className={MONO}>••••••••••••</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
    </div>
  );
}

function UserAlerts() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow className="border-warning/40">
        <span className="flex size-6 items-center justify-center rounded-lg bg-warning/10">
          <Bell className="size-3.5 text-warning" />
        </span>
        <div>
          <p className="text-3xs font-semibold text-text-primary">Password compromised</p>
          <p className="text-4xs text-text-tertiary">Seen in a public breach dump</p>
        </div>
      </MockRow>
      <div className="rounded-lg bg-brand py-1.5 text-center text-3xs font-semibold text-brand-foreground">Change password</div>
    </div>
  );
}

function PolicyEnforcement() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Password policy</p>
      {([
        ["12+ characters", true],
        ["Not in breach data", true],
        ["Rotated ≤ 90 days", false],
      ] as const).map(([rule, ok]) => (
        <div key={rule} className="flex items-center gap-2">
          {ok ? <Ok /> : <span className="flex size-4 items-center justify-center rounded-full bg-warning/15"><Clock className="size-2.5 text-warning" /></span>}
          <span className="text-3xs text-text-secondary">{rule}</span>
        </div>
      ))}
    </div>
  );
}

function VaultMonitoring() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow><Key className="size-3 text-text-quaternary" /><span className="text-3xs text-text-secondary">bank.com</span><span className="ml-auto"><Ok /></span></MockRow>
      <MockRow className="border-danger/40">
        <Key className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">shop.io</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Leaked</Chip>
      </MockRow>
      <MockRow><Key className="size-3 text-text-quaternary" /><span className="text-3xs text-text-secondary">mail.com</span><span className="ml-auto"><Ok /></span></MockRow>
    </div>
  );
}

function ApiSecurity() {
  return (
    <div className="w-full max-w-56 overflow-hidden rounded-lg bg-neutral-900 p-3 font-mono text-4xs leading-relaxed dark:bg-black/40">
      <p className="text-neutral-400"><span className="text-emerald-400">POST</span> /api/lookup/email/password/breaches</p>
      <p className="mt-1.5 text-neutral-500">{'{'}</p>
      <p className="pl-3 text-neutral-400">&quot;found&quot;: <span className="text-red-400">true</span>,</p>
      <p className="pl-3 text-neutral-400">&quot;count&quot;: <span className="text-amber-400">2</span></p>
      <p className="text-neutral-500">{'}'}</p>
    </div>
  );
}

function AccountProtection() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <span className="flex size-6 items-center justify-center rounded-lg bg-brand/10">
          <Shield className="size-3.5 text-brand" />
        </span>
        <div>
          <p className="text-3xs font-semibold text-text-primary">Patient portal</p>
          <p className="text-4xs text-text-tertiary">Sensitive · stricter screening</p>
        </div>
      </MockRow>
      <Chip size="sm" variant="soft" color="success" className="text-4xs">Breached passwords rejected</Chip>
    </div>
  );
}

function StuffingPrevention() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-end justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Login attempts</p>
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Attack blocked</Chip>
      </div>
      <div className="flex h-14 items-end gap-1">
        {[20, 25, 30, 90, 95, 85, 25].map((h, i) => (
          <div key={i} className={`flex-1 rounded-sm ${h > 50 ? "bg-danger/60" : "bg-neutral-200 dark:bg-neutral-700"}`} style={{ height: `${h}%` }} />
        ))}
      </div>
      <p className="text-4xs text-text-quaternary">Leaked credentials rejected: 4,182</p>
    </div>
  );
}

function SecurityAudits() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Org audit</p>
        <span className="text-sm font-bold text-warning">12%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
        <div className="h-full w-[12%] rounded-full bg-warning" />
      </div>
      <p className="text-4xs text-text-tertiary">38 of 312 employees use exposed passwords</p>
    </div>
  );
}

function PrivateChecks() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <Lock className="size-3 text-text-quaternary" />
        <span className={MONO}>••••••••</span>
      </MockRow>
      <div className="flex items-center gap-1.5 pl-1">
        <ArrowRight className="size-3 text-text-quaternary" />
        <span className="font-mono text-4xs text-text-tertiary">only SHA-1 hash sent: a94f2…</span>
      </div>
      <Chip size="sm" variant="soft" color="success" className="gap-1 text-4xs">
        <Shield className="size-2.5" /> k-anonymity: password stays on device
      </Chip>
    </div>
  );
}

function AdminProtection() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow>
        <User className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">member@corp.com</span>
        <Chip size="sm" variant="soft" className="ml-auto text-4xs">Standard</Chip>
      </MockRow>
      <MockRow className="border-brand/40">
        <Key className="size-3 text-brand" />
        <span className="text-3xs text-text-secondary">admin@corp.com</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Strict screening</Chip>
      </MockRow>
    </div>
  );
}

function ComplianceEvidence() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <FileText className="size-3.5 text-text-quaternary" />
        <p className="text-3xs font-semibold text-text-primary">Audit report · Q3</p>
      </div>
      {["Compromised-password control active", "100% of resets screened"].map((line) => (
        <div key={line} className="flex items-center gap-2">
          <Ok />
          <span className="text-3xs text-text-secondary">{line}</span>
        </div>
      ))}
      <div className="pt-0.5">
        <Chip size="sm" variant="soft" color="success" className="text-4xs">Auditor-ready evidence</Chip>
      </div>
    </div>
  );
}

/* ── Email-to-gender ─────────────────────────────────── */

function GenderPill({ g, p }: { g: "F" | "M"; p: number }) {
  return (
    <Chip size="sm" variant="soft" color="accent" className="gap-1 font-mono text-4xs">
      {g === "F" ? "Female" : "Male"} · {p}%
    </Chip>
  );
}

function GenderEnrichment() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <Mail className="size-3 text-text-quaternary" />
        <span className={MONO}>jane.doe@acme.com</span>
      </MockRow>
      <div className="flex justify-center">
        <ArrowRight className="size-3 rotate-90 text-text-quaternary" />
      </div>
      <MockRow>
        <div className="size-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <span className="text-3xs text-text-secondary">+ Gender field</span>
        <span className="ml-auto"><GenderPill g="F" p={98} /></span>
      </MockRow>
    </div>
  );
}

function AudienceAnalytics() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold uppercase tracking-wider text-text-tertiary">Newsletter reach</p>
      <div className="flex h-3 w-full overflow-hidden rounded-full">
        <div className="flex items-center justify-center bg-brand/70 text-4xs font-semibold text-white" style={{ width: "58%" }}>58%</div>
        <div className="flex items-center justify-center bg-neutral-300 text-4xs font-semibold text-text-secondary dark:bg-neutral-600" style={{ width: "42%" }}>42%</div>
      </div>
      <div className="flex justify-between text-4xs text-text-tertiary">
        <span>Female 58%</span>
        <span>Male 42%</span>
      </div>
    </div>
  );
}

function MarketingSegmentation() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow>
        <span className="text-3xs text-text-secondary">Women&apos;s collection</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">12,480</Chip>
      </MockRow>
      <MockRow>
        <span className="text-3xs text-text-secondary">Men&apos;s collection</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">9,020</Chip>
      </MockRow>
      <div className="flex justify-end pt-0.5">
        <span className="text-4xs text-text-quaternary">2 segments built</span>
      </div>
    </div>
  );
}

function ResearchSampling() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-end justify-between">
        <p className="text-3xs font-semibold uppercase tracking-wider text-text-tertiary">Respondents</p>
        <Chip size="sm" variant="soft" color="warning" className="text-4xs">Skew detected</Chip>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
        <div className="h-full w-[72%] rounded-full bg-warning" />
      </div>
      <p className="text-4xs text-text-tertiary">72% one group · rebalance before you run</p>
    </div>
  );
}

function CarefulPersonalization() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <MockRow>
        <span className="text-3xs text-text-secondary">Greeting</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Hi Jane</Chip>
      </MockRow>
      <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-white px-2.5 py-1.5 dark:bg-surface-raised">
        <span className="text-3xs text-text-secondary">Neutral fallback</span>
        <Chip size="sm" variant="soft" color="success" className="ml-auto text-4xs">On</Chip>
      </div>
      <p className="text-4xs text-text-quaternary">Users can always override</p>
    </div>
  );
}

function GenderedLocalization() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold uppercase tracking-wider text-text-tertiary">Spanish greeting</p>
      <MockRow className="opacity-60">
        <span className={MONO}>Bienvenido</span>
        <span className="ml-auto text-4xs text-text-quaternary">masc.</span>
      </MockRow>
      <MockRow className="border-brand/40">
        <span className={MONO}>Bienvenida</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Chosen · F</Chip>
      </MockRow>
    </div>
  );
}

function EcommerceRecommendations() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="size-8 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <div className="flex-1 space-y-1.5"><Bar w="60%" /><Bar w="40%" className="opacity-60" /></div>
        <GenderPill g="F" p={94} />
      </div>
      <div className="flex gap-1.5">
        {["Dresses", "Sizing", "Gift guide"].map((t) => (
          <Chip key={t} size="sm" variant="soft" color="accent" className="text-4xs">{t}</Chip>
        ))}
      </div>
    </div>
  );
}

function DataDeduplication() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow><span className={MONO}>j.doe@work.com</span><span className="ml-auto"><GenderPill g="F" p={97} /></span></MockRow>
      <MockRow><span className={MONO}>jane.d@gmail.com</span><span className="ml-auto"><GenderPill g="F" p={95} /></span></MockRow>
      <div className="flex items-center justify-center gap-1.5">
        <Users className="size-3 text-brand" />
        <span className="text-3xs font-medium text-brand">Likely same person · gender supports match</span>
      </div>
    </div>
  );
}

function DiversityReporting() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-3xs font-semibold uppercase tracking-wider text-text-tertiary">Applicant funnel</p>
        <FileText className="size-3 text-text-quaternary" />
      </div>
      <div className="flex h-14 items-end gap-2">
        <div className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-sm bg-brand/60" style={{ height: "70%" }} /><span className="text-5xs text-text-quaternary">F 46%</span></div>
        <div className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-sm bg-neutral-300 dark:bg-neutral-600" style={{ height: "80%" }} /><span className="text-5xs text-text-quaternary">M 51%</span></div>
        <div className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-sm bg-neutral-200 dark:bg-neutral-700" style={{ height: "20%" }} /><span className="text-5xs text-text-quaternary">? 3%</span></div>
      </div>
      <p className="text-4xs text-text-quaternary">Aggregate only · no individual labels stored</p>
    </div>
  );
}

function AdPerformanceAnalysis() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow>
        <span className="text-3xs text-text-secondary">Campaign A</span>
        <span className="ml-auto flex items-center gap-1"><span className="text-4xs text-text-quaternary">CTR</span><Chip size="sm" variant="soft" color="success" className="text-4xs">F ↑ 3.1%</Chip></span>
      </MockRow>
      <MockRow>
        <span className="text-3xs text-text-secondary">Campaign B</span>
        <span className="ml-auto flex items-center gap-1"><span className="text-4xs text-text-quaternary">CTR</span><Chip size="sm" variant="soft" color="accent" className="text-4xs">M ↓ 2.4%</Chip></span>
      </MockRow>
      <p className="text-4xs text-text-quaternary">Compare resonance, never exclude</p>
    </div>
  );
}

/* ── Malware URL scan ──────────────────────────────── */

function LinkScreening() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Inbound message</p>
      <div className="rounded-lg border border-danger/40 bg-white px-2.5 py-2 dark:bg-surface-raised">
        <div className="flex items-center gap-2">
          <Link className="size-3 text-text-quaternary" />
          <span className={`${MONO} truncate`}>bit.ly/free-gift</span>
          <span className="ml-auto"><Bad /></span>
        </div>
      </div>
      <Chip size="sm" variant="soft" color="danger" className="text-4xs">Blocked before click</Chip>
    </div>
  );
}

function PhishingDefense() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow className="border-danger/40">
        <Globe2 className="size-3 text-danger" />
        <span className={`${MONO} truncate`}>accounts-secure-login.co</span>
      </MockRow>
      <div className="flex items-center justify-between px-0.5">
        <span className="text-4xs text-text-quaternary">Lookalike of acme.com</span>
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Phishing</Chip>
      </div>
    </div>
  );
}

function RedirectUnmasking() {
  const hops = ["bit.ly/free-gift", "trk.ad-net.co/r", "accounts-secure-login.co"];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Redirect chain</p>
      {hops.map((h, i) => (
        <div key={h} className="flex items-center gap-1.5">
          <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-4xs text-text-tertiary dark:bg-neutral-700">{i + 1}</span>
          <span className={`${MONO} truncate ${i === hops.length - 1 ? "text-danger" : ""}`}>{h}</span>
          {i === hops.length - 1 && <span className="ml-auto"><Bad /></span>}
        </div>
      ))}
    </div>
  );
}

function UgcModeration() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="rounded-lg border border-neutral-200/70 bg-white px-2.5 py-2 dark:border-neutral-700/60 dark:bg-surface-raised">
        <p className="text-3xs text-text-secondary">“Check this out 👉 <span className="font-mono text-danger">short.ly/x9</span>”</p>
      </div>
      <div className="flex items-center justify-between px-0.5">
        <span className="text-4xs text-text-quaternary">User comment</span>
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Held for review</Chip>
      </div>
    </div>
  );
}

function SupportLinkTriage() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Ticket #4821</span>
        <Chip size="sm" variant="soft" color="warning" className="ml-auto text-4xs">Suspicious</Chip>
      </div>
      <MockRow>
        <Link className="size-3 text-text-quaternary" />
        <span className={`${MONO} truncate`}>invoice-pay.acme.co</span>
        <span className="ml-auto"><Eye className="size-3 text-text-quaternary" /></span>
      </MockRow>
      <p className="text-4xs text-text-quaternary">Verified without opening</p>
    </div>
  );
}

function AdVerification() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow className="border-success/40">
        <span className="text-3xs text-text-secondary">Advertiser URL</span>
        <span className="ml-auto"><Ok /></span>
      </MockRow>
      <div className="flex items-center justify-between px-0.5">
        <span className={`${MONO} truncate`}>go.brand-deal.com</span>
        <Chip size="sm" variant="soft" color="success" className="text-4xs">Clean</Chip>
      </div>
    </div>
  );
}

function FormLinkScanning() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Form submission</p>
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200/70 bg-white px-2.5 py-2 dark:border-neutral-700/60 dark:bg-surface-raised">
        <span className="text-4xs text-text-quaternary">website</span>
        <span className={`${MONO} truncate`}>my-portfolio.io</span>
        <span className="ml-auto"><Ok /></span>
      </div>
      <p className="text-4xs text-text-quaternary">Scanned before it&rsquo;s stored</p>
    </div>
  );
}

function ThreatEnrichment() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Verdict</span>
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Malicious · 92</Chip>
      </div>
      <div className="space-y-1 rounded-lg border border-neutral-200/70 bg-white p-2 dark:border-neutral-700/60 dark:bg-surface-raised">
        <div className="flex items-center gap-1.5"><Radar className="size-2.5 text-text-quaternary" /><span className={MONO}>2 domains</span></div>
        <div className="flex items-center gap-1.5"><Fingerprint className="size-2.5 text-text-quaternary" /><span className={MONO}>1 file hash</span></div>
      </div>
    </div>
  );
}

/* ── Malware QR scan ───────────────────────────────── */

function QrTile({ tone = "danger" }: { tone?: "danger" | "success" | "warning" }) {
  const border = tone === "success" ? "border-success/40" : tone === "warning" ? "border-warning/40" : "border-danger/40";
  return (
    <div className={`flex size-12 shrink-0 items-center justify-center rounded-lg border bg-white dark:bg-surface-raised ${border}`}>
      <QrCode className="size-7 text-text-secondary" />
    </div>
  );
}

function QuishingDefense() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <QrTile tone="danger" />
        <div className="min-w-0">
          <span className={`${MONO} block truncate`}>secure-login.co</span>
          <Chip size="sm" variant="soft" color="danger" className="mt-1 text-4xs">Quishing</Chip>
        </div>
      </div>
      <p className="text-4xs text-text-quaternary">Decoded, not scanned by phone</p>
    </div>
  );
}

function PosterVerification() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <QrTile tone="success" />
        <div className="min-w-0">
          <span className={`${MONO} block truncate`}>acme.com/event</span>
          <Chip size="sm" variant="soft" color="success" className="mt-1 text-4xs">Matches poster</Chip>
        </div>
      </div>
    </div>
  );
}

function PaymentQrChecks() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <QrTile tone="danger" />
        <div className="min-w-0">
          <span className={`${MONO} block truncate`}>pay.acme-invoice.co</span>
          <Chip size="sm" variant="soft" color="danger" className="mt-1 text-4xs">Tampered</Chip>
        </div>
      </div>
      <p className="text-4xs text-text-quaternary">Caught before money moves</p>
    </div>
  );
}

function MailroomScreening() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Inbound package</p>
      <div className="flex items-center gap-2.5">
        <QrTile tone="warning" />
        <div className="min-w-0">
          <span className={`${MONO} block truncate`}>track.parcel-x.co</span>
          <Chip size="sm" variant="soft" color="warning" className="mt-1 text-4xs">Review</Chip>
        </div>
      </div>
    </div>
  );
}

function EventCheckinSafety() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <QrTile tone="success" />
        <div className="min-w-0">
          <span className="text-3xs font-semibold text-text-secondary">Ticket #A-204</span>
          <Chip size="sm" variant="soft" color="success" className="mt-1 block w-fit text-4xs">Safe to admit</Chip>
        </div>
      </div>
    </div>
  );
}

function MenuSignageAudits() {
  const rows = [
    { id: "Table 4", tone: "success" as const, label: "OK" },
    { id: "Entrance", tone: "danger" as const, label: "Swapped" },
    { id: "Bar", tone: "success" as const, label: "OK" },
  ];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Venue codes</p>
      {rows.map((r) => (
        <MockRow key={r.id}>
          <QrCode className="size-3 text-text-quaternary" />
          <span className="text-3xs text-text-secondary">{r.id}</span>
          <Chip size="sm" variant="soft" color={r.tone === "danger" ? "danger" : "success"} className="ml-auto text-4xs">{r.label}</Chip>
        </MockRow>
      ))}
    </div>
  );
}

/* ── Malware image scan ────────────────────────────── */

function ScreenshotTriage() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center justify-center rounded-lg border border-neutral-200/70 bg-neutral-50 py-3 dark:border-neutral-700/60 dark:bg-surface-raised">
        <ImageIcon className="size-6 text-text-quaternary" />
      </div>
      <MockRow className="border-danger/40">
        <ExternalLink className="size-3 text-danger" />
        <span className={`${MONO} truncate`}>login-verify.co</span>
        <span className="ml-auto"><Bad /></span>
      </MockRow>
    </div>
  );
}

function AttachmentScanning() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow>
        <ImageIcon className="size-3 text-text-quaternary" />
        <span className="text-3xs text-text-secondary">receipt.png</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">OCR</Chip>
      </MockRow>
      <MockRow className="border-danger/40">
        <span className={`${MONO} truncate`}>pay-now.acme-bill.co</span>
        <span className="ml-auto"><Bad /></span>
      </MockRow>
    </div>
  );
}

function SocialMonitoring() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Brand mention</p>
      <div className="flex items-center gap-2 rounded-lg border border-danger/40 bg-white px-2.5 py-2 dark:bg-surface-raised">
        <ImageIcon className="size-3 text-text-quaternary" />
        <span className={`${MONO} truncate`}>acme-rewards.link</span>
        <span className="ml-auto"><Bad /></span>
      </div>
      <p className="text-4xs text-text-quaternary">Link hidden inside an image</p>
    </div>
  );
}

function ChatImageModeration() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="ml-auto w-fit rounded-lg rounded-br-sm bg-brand/10 px-2.5 py-2">
        <ImageIcon className="size-5 text-text-tertiary" />
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Link blocked</Chip>
      </div>
    </div>
  );
}

function DocumentLinkExtraction() {
  const links = ["docs.acme.com/tos", "cdn.acme.com/dl", "track.promo.co"];
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Links found · 3</p>
      {links.map((l, i) => (
        <MockRow key={l}>
          <ExternalLink className="size-3 text-text-quaternary" />
          <span className={`${MONO} truncate`}>{l}</span>
          <span className="ml-auto">{i === 2 ? <Bad /> : <Ok />}</span>
        </MockRow>
      ))}
    </div>
  );
}

function BrandAbuseDetection() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2.5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-danger/40 bg-white dark:bg-surface-raised">
          <ImageIcon className="size-5 text-text-quaternary" />
        </div>
        <div className="min-w-0">
          <span className="text-3xs text-text-secondary">Impersonates your brand</span>
          <Chip size="sm" variant="soft" color="danger" className="mt-1 block w-fit text-4xs">Lookalike</Chip>
        </div>
      </div>
    </div>
  );
}

/* ── GitHub leaks ──────────────────────────────────── */

function RepoAudits() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <Code className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-secondary">acme/checkout</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">3 secrets</Chip>
      </div>
      <MockRow className="border-danger/40">
        <span className={`${MONO} truncate`}>config/prod.env</span>
        <span className="ml-auto text-4xs text-danger">L12</span>
      </MockRow>
    </div>
  );
}

function PreMergeChecks() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-2">
        <BranchFilled className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-secondary">PR #128</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Blocked</Chip>
      </div>
      <MockRow className="border-danger/40">
        <Key className="size-3 text-danger" />
        <span className={`${MONO} truncate`}>aws-access-key</span>
        <span className="ml-auto"><Bad /></span>
      </MockRow>
    </div>
  );
}

function HistorySweeps() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Git history</p>
      {[{ c: "a1f9", ok: true }, { c: "7c22", ok: true }, { c: "3e04", ok: false }].map((r) => (
        <MockRow key={r.c}>
          <span className={`${MONO}`}>{r.c}</span>
          <Bar className="flex-1" />
          <span className="ml-auto">{r.ok ? <Ok /> : <Bad />}</span>
        </MockRow>
      ))}
      <p className="text-4xs text-text-quaternary">Secret removed in a later commit</p>
    </div>
  );
}

function AcquisitionDueDiligence() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Target repos</span>
        <Chip size="sm" variant="soft" color="warning" className="text-4xs">Risk: med</Chip>
      </div>
      <MockRow><span className="text-3xs text-text-secondary">14 repos</span><span className="ml-auto text-4xs text-text-quaternary">2 with leaks</span></MockRow>
    </div>
  );
}

function ContractorOffboarding() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <User className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-secondary">j.doe left</span>
        <Chip size="sm" variant="soft" color="accent" className="ml-auto text-4xs">Sweep</Chip>
      </div>
      <MockRow><span className={`${MONO} truncate`}>infra/deploy.sh</span><span className="ml-auto"><Bad /></span></MockRow>
      <p className="text-4xs text-text-quaternary">1 key to rotate</p>
    </div>
  );
}

function IncidentResponseGh() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-1.5">
        <Key className="size-3 text-danger" />
        <span className={`${MONO} truncate`}>sk_live_…a91</span>
        <ArrowRight className="size-3 text-text-quaternary" />
        <Code className="size-3 text-text-quaternary" />
      </div>
      <Chip size="sm" variant="soft" color="danger" className="text-4xs">Traced to public repo</Chip>
    </div>
  );
}

/* ── Exposed API keys ──────────────────────────────── */

function KeyLeakMonitoring() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <Radar className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Live monitor</span>
        <span className="ml-auto flex size-2 rounded-full bg-success" />
      </div>
      <MockRow className="border-danger/40">
        <Key className="size-3 text-danger" />
        <span className={`${MONO} truncate`}>stripe_secret</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Live</Chip>
      </MockRow>
    </div>
  );
}

function PasteSiteSweeps() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <MockRow>
        <Search className="size-3 text-text-quaternary" />
        <span className={`${MONO} truncate`}>gist.github.com/xxxx</span>
      </MockRow>
      <div className="flex items-center justify-between px-0.5">
        <span className="text-4xs text-text-quaternary">Matched to acme.com</span>
        <Chip size="sm" variant="soft" color="danger" className="text-4xs">Exposed</Chip>
      </div>
    </div>
  );
}

function SecretRotation() {
  return (
    <div className="w-full max-w-56 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className={`${MONO} truncate line-through opacity-60`}>ak_9f2…</span>
        <ArrowRight className="size-3 text-text-quaternary" />
        <span className={`${MONO} truncate text-success`}>ak_new…</span>
      </div>
      <Chip size="sm" variant="soft" color="success" className="text-4xs">Rotated</Chip>
    </div>
  );
}

function VendorKeyExposure() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <Server className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-secondary">vendor-x</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Key leaked</Chip>
      </div>
      <p className="text-4xs text-text-quaternary">Grants access to your data</p>
    </div>
  );
}

function BugBountyTriage() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <div className="flex items-center gap-2">
        <FileText className="size-3 text-text-quaternary" />
        <span className="text-3xs font-semibold text-text-secondary">Report #56</span>
        <Chip size="sm" variant="soft" color="danger" className="ml-auto text-4xs">Confirmed live</Chip>
      </div>
      <MockRow><Key className="size-3 text-text-quaternary" /><span className={`${MONO} truncate`}>gh_pat_…</span><span className="ml-auto"><Bad /></span></MockRow>
    </div>
  );
}

function ComplianceEvidenceKeys() {
  return (
    <div className="w-full max-w-56 space-y-1.5">
      <p className="text-3xs font-semibold text-text-tertiary uppercase tracking-wider">Audit log</p>
      {["Monitoring active", "0 keys live", "12 rotated"].map((t) => (
        <MockRow key={t}><Ok /><span className="text-3xs text-text-secondary">{t}</span></MockRow>
      ))}
    </div>
  );
}

/* ── Registry ──────────────────────────────────────── */

const MOCKUPS: Record<string, () => ReactNode> = {
  "Signup Validation": SignupValidation,
  "List Cleaning": ListCleaning,
  "Bounce Reduction": BounceReduction,
  "CRM Hygiene": CrmHygiene,
  "Fraud Screening": FraudScreening,
  "Recovery Checks": RecoveryChecks,
  "Seller Verification": SellerVerification,
  "Typo Correction": TypoCorrection,
  "Trial Abuse": TrialAbuse,
  "Address Updates": AddressUpdates,
  // Email-to-person
  "Lead Enrichment": LeadEnrichment,
  "Form Identification": FormIdentification,
  "Account Matching": AccountMatching,
  "Support Context": SupportContext,
  "CRM Enrichment": CrmEnrichment,
  "Investor Research": InvestorResearch,
  "Candidate Research": CandidateResearch,
  "Abuse Investigation": AbuseInvestigation,
  "Employer Verification": EmployerVerification,
  "Personalized Onboarding": PersonalizedOnboarding,
  "Duplicate Detection": DuplicateDetection,
  "Lead Routing": LeadRouting,
  // Email breach
  "Breach Alerts": BreachAlerts,
  "Risk Scoring": RiskScoring,
  "Password Reset": PasswordReset,
  "MFA Enforcement": MfaEnforcement,
  "Employee Monitoring": EmployeeMonitoring,
  "Vendor Assessment": VendorAssessment,
  "Identity Protection": IdentityProtection,
  "Breach Reporting": BreachReporting,
  "Incident Investigation": IncidentInvestigation,
  "Cyber Underwriting": CyberUnderwriting,
  "Domain Monitoring": DomainMonitoring,
  "Executive Protection": ExecutiveProtection,
  // Password breach
  "Password Screening": PasswordScreening,
  "Reset Screening": ResetScreening,
  "User Alerts": UserAlerts,
  "Policy Enforcement": PolicyEnforcement,
  "Vault Monitoring": VaultMonitoring,
  "API Security": ApiSecurity,
  "Account Protection": AccountProtection,
  "Stuffing Prevention": StuffingPrevention,
  "Security Audits": SecurityAudits,
  "Private Checks": PrivateChecks,
  "Admin Protection": AdminProtection,
  "Compliance Evidence": ComplianceEvidence,
  // Email-to-gender
  "Gender Enrichment": GenderEnrichment,
  "Audience Analytics": AudienceAnalytics,
  "Marketing Segmentation": MarketingSegmentation,
  "Research Sampling": ResearchSampling,
  "Careful Personalization": CarefulPersonalization,
  "Gendered Localization": GenderedLocalization,
  "Ecommerce Recommendations": EcommerceRecommendations,
  "Data Deduplication": DataDeduplication,
  "Diversity Reporting": DiversityReporting,
  "Ad Performance Analysis": AdPerformanceAnalysis,
  // Malware URL scan
  "Link Screening": LinkScreening,
  "Phishing Defense": PhishingDefense,
  "Redirect Unmasking": RedirectUnmasking,
  "UGC Moderation": UgcModeration,
  "Support Link Triage": SupportLinkTriage,
  "Ad Verification": AdVerification,
  "Form Link Scanning": FormLinkScanning,
  "Threat Enrichment": ThreatEnrichment,
  // Malware QR scan
  "Quishing Defense": QuishingDefense,
  "Poster Verification": PosterVerification,
  "Payment QR Checks": PaymentQrChecks,
  "Mailroom Screening": MailroomScreening,
  "Event Check-in Safety": EventCheckinSafety,
  "Menu & Signage Audits": MenuSignageAudits,
  // Malware image scan
  "Screenshot Triage": ScreenshotTriage,
  "Attachment Scanning": AttachmentScanning,
  "Social Monitoring": SocialMonitoring,
  "Chat Image Moderation": ChatImageModeration,
  "Document Link Extraction": DocumentLinkExtraction,
  "Brand Abuse Detection": BrandAbuseDetection,
  // GitHub leaks
  "Repo Audits": RepoAudits,
  "Pre-Merge Checks": PreMergeChecks,
  "History Sweeps": HistorySweeps,
  "Acquisition Due Diligence": AcquisitionDueDiligence,
  "Contractor Offboarding": ContractorOffboarding,
  "Incident Response": IncidentResponseGh,
  // Exposed API keys
  "Key Leak Monitoring": KeyLeakMonitoring,
  "Paste-Site Sweeps": PasteSiteSweeps,
  "Secret Rotation": SecretRotation,
  "Vendor Key Exposure": VendorKeyExposure,
  "Bug-Bounty Triage": BugBountyTriage,
  "Leak Audit Trail": ComplianceEvidenceKeys,
};

export function hasUseCaseMockup(name: string): boolean {
  return name in MOCKUPS;
}

/** White (dark: surface) inner panel with the mockup centred inside. */
export function UseCaseMockup({
  name,
  className = "",
  fallback = null,
}: {
  name: string;
  className?: string;
  /** Rendered when no mockup is registered (lets server components skip hasUseCaseMockup). */
  fallback?: ReactNode;
}) {
  const Mock = MOCKUPS[name];
  if (!Mock) return <>{fallback}</>;
  return (
    <div className={`flex items-center justify-center overflow-hidden bg-white p-5 dark:bg-surface-base ${className}`}>
      <Mock />
    </div>
  );
}
